import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal.jsx";
import EarlyExitBreakdown from "../components/EarlyExitBreakdown.jsx";
import FlowModal from "../components/FlowModal.jsx";
import LtvBadge from "../components/LtvBadge.jsx";
import ResultModal from "../components/ResultModal.jsx";
import Shimmer from "../components/Shimmer.jsx";
import StatRow from "../components/StatRow.jsx";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { useChainTime } from "../hooks/useChainTime.js";
import { useDeposits, DEPOSIT_STATE } from "../hooks/useDeposits.js";
import { getContract } from "../services/contracts.js";
import { getTxOverrides } from "../services/gas.js";
import { resyncDeposit } from "../services/api.js";
import { decodeContractError } from "../utils/contractError.js";
import { formatUsdcCompact, liveAccruedInterestSmooth } from "../utils/format.js";
import { chainNowSec as chainClockNowSec } from "../utils/chainClock.js";

// Tab membership is computed via `matches(deposit, nowSec)` instead of
// a plain `state in [...]` because on-chain `state` only flips to
// Matured (2) when someone explicitly calls `markMatured()` — until
// then the row reads as Active even though chain time has crossed
// maturityTime. Routing those "matured-but-state-still-1" rows into
// the Matured tab keeps the UI in sync with reality (the contract
// will treat them as Matured on the next settle/rollover call) and
// matches the same logic that drives the Settle/Rollover CTAs.
const TABS = [
    {
        id: "active",
        label: "Active",
        // Active = state 1 AND chain hasn't crossed maturity yet.
        matches: (d, nowSec) =>
            Number(d.state) === 1 && nowSec < Number(d.maturityTime || 0)
    },
    {
        id: "matured",
        label: "Matured",
        // Matured = explicit state 2/3/4 OR state 1 past maturityTime.
        matches: (d, nowSec) => {
            const s = Number(d.state);
            if (s === 2 || s === 3 || s === 4) return true;
            return s === 1 && nowSec >= Number(d.maturityTime || 0);
        }
    },
    {
        id: "closed",
        label: "Closed",
        matches: (d) => {
            const s = Number(d.state);
            return s === 5 || s === 6;
        }
    }
];

// Sort options. Each entry knows how to compare two deposit rows so
// the same option block can be reused for a future per-column header
// click without reshuffling state.
// LTV computed from `loanBalance / safeVault` where safeVault = principal × 80%.
// Mirrors the same formula the LtvBadge + MyLoans page use so the filter
// chip matches the row badge the user is reading on screen.
function computeLtvPct(d) {
    const safeVault = (BigInt(d.principal) * BigInt(config.protocol.safeVaultBps)) / 10000n;
    if (safeVault === 0n) return 0;
    return Math.round(Number((BigInt(d.loanBalance) * 10000n) / safeVault) / 100);
}

const SORT_OPTIONS = [
    {
        id: "newest",
        label: "Newest first",
        compare: (a, b) => Number(b.startTime) - Number(a.startTime)
    },
    {
        id: "oldest",
        label: "Oldest first",
        compare: (a, b) => Number(a.startTime) - Number(b.startTime)
    },
    {
        id: "principal_desc",
        label: "Largest principal",
        compare: (a, b) => bigCmp(BigInt(b.principal), BigInt(a.principal))
    },
    {
        id: "principal_asc",
        label: "Smallest principal",
        compare: (a, b) => bigCmp(BigInt(a.principal), BigInt(b.principal))
    },
    {
        id: "accrued_desc",
        label: "Most accrued",
        compare: (a, b) => bigCmp(BigInt(b.accruedInterest), BigInt(a.accruedInterest))
    },
    {
        id: "maturity_asc",
        label: "Soonest to mature",
        compare: (a, b) => Number(a.maturityTime) - Number(b.maturityTime)
    },
    {
        id: "loan_desc",
        label: "Largest loan",
        compare: (a, b) => bigCmp(BigInt(b.loanBalance), BigInt(a.loanBalance))
    },
    {
        id: "loan_asc",
        label: "Smallest loan",
        compare: (a, b) => bigCmp(BigInt(a.loanBalance), BigInt(b.loanBalance))
    },
    {
        id: "ltv_desc",
        label: "Highest LTV",
        compare: (a, b) => computeLtvPct(b) - computeLtvPct(a)
    },
    {
        id: "ltv_asc",
        label: "Lowest LTV",
        compare: (a, b) => computeLtvPct(a) - computeLtvPct(b)
    }
];

const COMPOUND_FILTERS = [
    { id: "all", label: "All modes", match: () => true },
    { id: "off", label: "Off", match: (d) => Number(d.compoundMode) === 0 },
    { id: "standard", label: "Standard", match: (d) => Number(d.compoundMode) === 1 },
    { id: "turbo", label: "Turbo", match: (d) => Number(d.compoundMode) === 2 }
];

const LOAN_FILTERS = [
    { id: "any", label: "Any loan state", match: () => true },
    { id: "with_loan", label: "With outstanding loan", match: (d) => BigInt(d.loanBalance) > 0n },
    { id: "no_loan", label: "No loan", match: (d) => BigInt(d.loanBalance) === 0n }
];

// Match MyLoans' LTV bands exactly so a user reading both pages sees the
// same thresholds. Rows with no loan fall into the "Safe" band (LTV = 0).
const LTV_FILTERS = [
    { id: "all", label: "All LTV", match: () => true },
    { id: "safe", label: "Safe (< 50%)", match: (d) => computeLtvPct(d) < 50 },
    { id: "watch", label: "Watch (50–65%)", match: (d) => {
        const pct = computeLtvPct(d);
        return pct >= 50 && pct < 65;
    } },
    { id: "risky", label: "Risky (≥ 65%)", match: (d) => computeLtvPct(d) >= 65 }
];

function bigCmp(a, b) {
    if (a > b) return 1;
    if (a < b) return -1;
    return 0;
}

/**
 * "My TD's" listing page. Top stat strip mirrors the Dashboard so the
 * user always sees the same headline numbers, and the table below
 * exposes every position the wallet owns with type / accrued interest /
 * maturity countdown / auto-compound mode columns.
 *
 * Real deposit rows come from the on-chain `useDeposits` hook; placeholder
 * data shows up only when the wallet is empty so the design stays
 * legible during the first session before any deposits exist.
 */
export default function MyTds() {
    const { address, signer } = useWallet();
    const { deposits, isLoading, refresh, patchDeposit } = useDeposits(address);
    // Chain time — drives the tab membership for "matured-but-state-1"
    // rows. See the TABS definition above for context.
    const { chainNowSec } = useChainTime();
    const [tab, setTab] = useState("active");
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState("newest");
    const [filterCompound, setFilterCompound] = useState("all");
    const [filterLoan, setFilterLoan] = useState("any");
    const [filterLtv, setFilterLtv] = useState("all");
    // Local override map for the per-row Automated Reinvestment mode. Keyed by
    // depositId so the table state survives between renders. Populated
    // optimistically before the on-chain `setCompoundMode` confirms,
    // then cleared when the deposit row's compoundMode reflects the
    // new value (via the next `useDeposits` refresh).
    const [compoundOverrides, setCompoundOverrides] = useState({});
    const [compoundBusyId, setCompoundBusyId] = useState(null);
    const [compoundError, setCompoundError] = useState(null);
    const [compoundSuccess, setCompoundSuccess] = useState(null);
    const [compoundFlowOpen, setCompoundFlowOpen] = useState(false);
    const [compoundSteps, setCompoundSteps] = useState([]);

    // Early-exit flow state. `exitTarget` is the deposit row the user is
    // confirming against (null when no modal open); `exitBusy` flips
    // while the on-chain tx is in flight; `exitSteps` drives the wallet
    // FlowModal; `exitResult` drives the success/error modal afterwards.
    const [exitTarget, setExitTarget] = useState(null);
    const [exitBusy, setExitBusy] = useState(false);
    const [exitFlowOpen, setExitFlowOpen] = useState(false);
    const [exitSteps, setExitSteps] = useState([]);
    const [exitResult, setExitResult] = useState(null);

    // DEPRECATED in V6.2 strict — pool-driven sweep replaces this. The
    // state hooks remain unused but harmless and will be removed once a
    // wider Reinvest UX refresh ships.
    // Was: §17 manual Compound Now flow. The backend keeper auto-triggers
    // executeCompound on a 5-min cadence, but a user who's just watched
    // their accrual cross 100 USDC shouldn't have to wait — the contract
    // is permissionless, so we surface a button that fires the same call
    // from their own wallet.
    const [compoundNowBusyId, setCompoundNowBusyId] = useState(null);
    const [compoundNowFlowOpen, setCompoundNowFlowOpen] = useState(false);
    const [compoundNowSteps, setCompoundNowSteps] = useState([]);
    const [compoundNowResult, setCompoundNowResult] = useState(null);

    /**
     * Persists the auto-compound mode on-chain via the AutoCompound
     * contract (which mirrors the value back to the deposit struct via
     * BondContract.setCompoundMode). UI updates optimistically + clears
     * the override once the next refresh has the on-chain value.
     *
     * `mode` is the numeric CompoundMode enum: 0 Off, 1 Standard, 2 Turbo.
     */
    function setCompoundStepStatus(key, status) {
        setCompoundSteps((prev) => prev.map((s) => (s.key === key ? { ...s, status } : s)));
    }

    async function setCompoundMode(id, mode) {
        const modeNum = typeof mode === "number" ? mode : Number(mode);
        if (!signer) return;
        const modeLabel = modeNum === 2 ? "Turbo" : modeNum === 1 ? "Standard" : "Off";
        setCompoundBusyId(id);
        setCompoundSteps([
            {
                key: "confirm",
                label: `Confirm ${modeLabel} in wallet`,
                doneLabel: "Wallet confirmed",
                status: "loading"
            },
            {
                key: "mine",
                label: "Waiting for transaction",
                doneLabel: "Transaction mined",
                status: "pending"
            }
        ]);
        setCompoundFlowOpen(true);
        try {
            // §I.6 V6.2 strict — the standalone AutoCompound contract is
            // deprecated. `setCompoundMode` now lives directly on the
            // BondContract; the deposit owner sets their own mode and the
            // CYR keeper handles the pool-based sweep.
            const bond = (await import("../services/contracts.js")).getContract("bond", signer);
            // No optimistic paint here — the Type pill + toggle must keep
            // showing the current mode while the wallet prompt is open so
            // that "selecting Turbo" doesn't visually claim Turbo before
            // the user has confirmed the transaction (or might reject it).
            const tx = await bond.setCompoundMode(id, modeNum);
            setCompoundStepStatus("confirm", "done");
            setCompoundStepStatus("mine", "loading");
            await tx.wait();
            setCompoundStepStatus("mine", "done");
            // Tx confirmed — paint the new mode via the override. We
            // intentionally do NOT call patchDeposit here: doing so
            // would mutate the in-memory deposit and trigger the
            // cleanup useEffect (deps: [deposits]) which would see
            // `deposit.compoundMode === override` and immediately drop
            // the override. Then the awaited refresh() below — which
            // typically still returns the stale CompoundMode (the
            // CompoundModeSet event hasn't been indexed yet) — would
            // overwrite the row, snapping the toggle back to its old
            // value. Letting the override carry the new mode forward
            // until canonical state agrees is the whole point of the
            // override map.
            setCompoundOverrides((prev) => ({ ...prev, [id]: modeNum }));
            // §V6.2 — older contract builds don't emit CompoundModeSet
            // (the event was added in this branch), so the indexer can't
            // observe a setCompoundMode tx on those deployments. Ask the
            // backend to re-pull the deposit straight from chain so the
            // canonical row in Mongo carries the new mode. On a freshly-
            // redeployed contract this is just a redundant safety net —
            // the indexer listener fills the same gap.
            resyncDeposit(id).catch((err) =>
                console.warn("setCompoundMode: resync failed", err?.message || err)
            );
            await refresh();
            // Safety net for backend RPC lag: the CompoundModeSet event
            // becomes indexable once it clears the confirmation buffer,
            // so a refresh a few seconds later usually carries the new
            // value even when the initial sync didn't.
            setTimeout(() => {
                refresh().catch(() => {});
            }, 5000);
            setCompoundFlowOpen(false);
            setCompoundSuccess({
                title: "Automated Reinvestment updated",
                message: `Term Deposit #${id} is now set to ${modeLabel}.`
            });
        } catch (err) {
            // Mark the in-flight step as errored, close the flow, then
            // surface the failure in a result modal — previously this
            // was swallowed and the toggle silently snapped back with
            // no explanation.
            setCompoundSteps((prev) =>
                prev.map((s) => (s.status === "loading" ? { ...s, status: "error" } : s))
            );
            setCompoundFlowOpen(false);
            setCompoundError(decodeContractError(err));
        } finally {
            setCompoundBusyId(null);
        }
    }

    // Once the canonical deposit row actually reflects an override, we
    // can safely retire that override — keeps the UI consistent without
    // a manual clear on every refresh path. If the row still shows the
    // old mode (backend RPC lag), the override stays and the optimistic
    // value remains visible until the indexer / next refresh catches up.
    useEffect(() => {
        setCompoundOverrides((prev) => {
            const keys = Object.keys(prev);
            if (keys.length === 0) return prev;
            let changed = false;
            const next = {};
            for (const key of keys) {
                const deposit = deposits.find((d) => String(d.id) === key);
                if (deposit && Number(deposit.compoundMode) === Number(prev[key])) {
                    changed = true; // canonical caught up — drop override
                } else {
                    next[key] = prev[key];
                }
            }
            return changed ? next : prev;
        });
    }, [deposits]);

    function requestEarlyExit(row) {
        // Pre-flight check: contract reverts with OutstandingLoan if the
        // deposit still has a non-zero loanBalance. Block here before we
        // even open the confirm modal so the user sees a clear message
        // instead of an opaque "execution reverted" from the wallet.
        if (BigInt(row.loanBalance) > 0n) {
            setExitResult({
                type: "error",
                title: "Repay loan first",
                message: `Term Deposit #${row.id} has an outstanding loan of $${formatUsdcCompact(
                    row.loanBalance
                )} USDC. Repay it from the My Loans page before exiting this deposit.`
            });
            return;
        }
        setExitTarget(row);
    }

    function cancelEarlyExit() {
        if (exitBusy) return;
        setExitTarget(null);
    }

    function setStepStatus(key, status) {
        setExitSteps((prev) => prev.map((s) => (s.key === key ? { ...s, status } : s)));
    }

    async function confirmEarlyExit() {
        if (!signer || !exitTarget) return;
        const target = exitTarget;
        setExitBusy(true);
        const initialSteps = [
            {
                key: "exit",
                label: `Closing Term Deposit #${target.id}`,
                doneLabel: `Closed Term Deposit #${target.id}`,
                status: "loading"
            }
        ];
        setExitSteps(initialSteps);
        setExitFlowOpen(true);
        setExitResult(null);

        try {
            const bond = getContract("bond", signer);
            const overrides = await getTxOverrides(signer.provider);
            const tx = await bond.earlyExit(target.id, overrides);
            await tx.wait();
            setStepStatus("exit", "done");

            // Optimistic update: chain has already moved this deposit to
            // Exited, but the indexer can take up to one poll cycle (~15s)
            // to mirror that into Mongo, and the API endpoint reads from
            // Mongo. Patch the local row immediately so the UI moves it
            // out of the Active tab the moment the wallet confirms,
            // instead of leaving the user staring at a "stale" row.
            patchDeposit(target.id, { state: DEPOSIT_STATE.Exited });

            setExitFlowOpen(false);
            setExitTarget(null);
            // §V6.2 — penalty % is per-product (Standard 20%, Turbo
            // 50%). Read from the row's stored earlyExitPenaltyBps so
            // a Turbo deposit's success copy doesn't claim 20% when
            // the chain actually routed 50% to the Distribution Reserve.
            const penaltyPct =
                (Number(target.earlyExitPenaltyBps) ||
                    (Number(target.productMode) === 1 ? 5000 : 2000)) / 100;
            setExitResult({
                type: "success",
                title: "Early Exit Complete",
                message: `Term Deposit #${target.id} has been closed. A ${penaltyPct}% penalty on the principal was routed to the Distribution Reserve; the remaining principal plus accrued interest has been returned to your wallet.`
            });

            // Three-layer persistence — same pattern as openDeposit:
            //   1. API fast-path: backend pulls fresh state from chain and
            //      writes it to Mongo right now. (this call)
            // Background reconciliation — replaces the optimistic patch with
            // the canonical indexed state once the backend catches up. We
            // don't await it so the success modal isn't blocked.
            refresh().catch(() => {
                /* ignore — we already showed success */
            });
        } catch (err) {
            setStepStatus("exit", "error");
            setExitFlowOpen(false);
            setExitResult({
                type: "error",
                title: "Early Exit Failed",
                message: decodeContractError(err)
            });
        } finally {
            setExitBusy(false);
        }
    }

    /**
     * Manually trigger `AutoCompound.executeCompound(depositId)` from the
     * connected wallet. The function is permissionless on-chain, so the
     * user pays gas and the new (Standard or Turbo) deposit is minted to
     * them directly. Mirrors the same FlowModal/ResultModal pattern as
     * the early-exit flow so the user gets clear wallet-prompt / mining
     * / success feedback.
     */
    function setCompoundNowStepStatus(key, status) {
        setCompoundNowSteps((prev) => prev.map((s) => (s.key === key ? { ...s, status } : s)));
    }

    async function triggerCompoundNow(row) {
        if (!signer) return;
        const id = row.id;
        const modeLabel = Number(row.compoundMode) === 2 ? "Turbo" : "Standard";
        setCompoundNowBusyId(id);
        setCompoundNowSteps([
            {
                key: "confirm",
                label: `Confirm ${modeLabel} compound in wallet`,
                doneLabel: "Wallet confirmed",
                status: "loading"
            },
            {
                key: "mine",
                label: "Compounding interest into a new deposit",
                doneLabel: "Compound transaction mined",
                status: "pending"
            }
        ]);
        setCompoundNowFlowOpen(true);
        try {
            const autoCompound = getContract("autoCompound", signer);
            const overrides = await getTxOverrides(signer.provider);
            const tx = await autoCompound.executeCompound(id, overrides);
            setCompoundNowStepStatus("confirm", "done");
            setCompoundNowStepStatus("mine", "loading");
            await tx.wait();
            setCompoundNowStepStatus("mine", "done");
            setCompoundNowFlowOpen(false);
            setCompoundNowResult({
                type: "success",
                title: "Compound executed",
                message: `Term Deposit #${id}'s accrued interest has been reinvested into a new ${modeLabel.toLowerCase()} deposit. It will appear in your active list shortly.`
            });
            // Backend indexer needs a poll cycle to pick up the new
            // deposit row; nudge an extra refresh after a few seconds.
            refresh().catch(() => {});
            setTimeout(() => refresh().catch(() => {}), 5000);
        } catch (err) {
            setCompoundNowSteps((prev) =>
                prev.map((s) => (s.status === "loading" ? { ...s, status: "error" } : s))
            );
            setCompoundNowFlowOpen(false);
            setCompoundNowResult({
                type: "error",
                title: "Compound failed",
                message: decodeContractError(err)
            });
        } finally {
            setCompoundNowBusyId(null);
        }
    }

    // Headline totals reflect only the Active (state=1) positions. Once a
    // deposit is settled or early-exited the user already has the funds
    // back in their wallet, so it shouldn't keep inflating the strip.
    const totals = useMemo(() => {
        return deposits
            .filter((d) => Number(d.state) === 1)
            .reduce(
                (acc, d) => ({
                    principal: acc.principal + BigInt(d.principal),
                    interest: acc.interest + BigInt(d.accruedInterest),
                    loan: acc.loan + BigInt(d.loanBalance)
                }),
                { principal: 0n, interest: 0n, loan: 0n }
            );
    }, [deposits]);

    // Counts use the same chain-time-aware `matches()` predicate the
    // row filter does, so the tab badge always agrees with the table
    // body (a deposit past maturityTime shows under Matured, not
    // Active, even if `state` still reads 1).
    const counts = useMemo(() => {
        const out = { active: 0, matured: 0, closed: 0 };
        for (const d of deposits) {
            for (const t of TABS) {
                if (t.matches(d, chainNowSec)) {
                    out[t.id] += 1;
                    break;
                }
            }
        }
        return out;
    }, [deposits, chainNowSec]);

    const rows = useMemo(() => {
        const tabConfig = TABS.find((t) => t.id === tab);
        const compound = COMPOUND_FILTERS.find((o) => o.id === filterCompound) || COMPOUND_FILTERS[0];
        const loan = LOAN_FILTERS.find((o) => o.id === filterLoan) || LOAN_FILTERS[0];
        const ltv = LTV_FILTERS.find((o) => o.id === filterLtv) || LTV_FILTERS[0];
        const sort = SORT_OPTIONS.find((o) => o.id === sortKey) || SORT_OPTIONS[0];
        const q = search.trim().toLowerCase();
        // Tab → keyword search → compound mode → loan state → LTV band → sort.
        // Building the array once and sorting in place at the end is
        // cheaper than re-filtering on each comparator call.
        const filtered = deposits.filter((d) => {
            if (!tabConfig.matches(d, chainNowSec)) return false;
            if (q && !String(d.id).includes(q)) return false;
            if (!compound.match(d)) return false;
            if (!loan.match(d)) return false;
            if (!ltv.match(d)) return false;
            return true;
        });
        return [...filtered].sort(sort.compare);
    }, [deposits, tab, search, sortKey, filterCompound, filterLoan, filterLtv, chainNowSec]);

    function clearFilters() {
        setFilterCompound("all");
        setFilterLoan("any");
        setFilterLtv("all");
    }
    const filtersApplied =
        filterCompound !== "all" || filterLoan !== "any" || filterLtv !== "all";

    const activeCount = counts.active;

    // Count of abandoned deposits (state=4). Drives the "Recover abandoned"
    // call-out at the top of the page so users can jump straight to the
    // dedicated recovery surface — TdDetail still owns the per-position
    // recovery action, but a sweep-style page is much easier to discover
    // when the user hasn't visited the deposit in months.
    const abandonedCount = useMemo(
        () => deposits.filter((d) => Number(d.state) === DEPOSIT_STATE.Abandoned).length,
        [deposits]
    );

    return (
        <div className="-mt-6 space-y-5">
            <StatRow
                totals={totals}
                activeCount={activeCount}
                deposits={deposits}
                isLoading={isLoading}
            />

            {abandonedCount > 0 ? (
                <Link
                    to="/term-deposits/recovery"
                    className="flex items-center justify-between gap-3 rounded-[10px] border border-[#C93030]/30 bg-[#C93030]/5 px-4 py-3 text-[#10281b] hover:bg-[#C93030]/10 transition-colors"
                >
                    <div className="min-w-0">
                        <div className="font-manrope text-[13px] font-bold">
                            {abandonedCount} abandoned deposit{abandonedCount === 1 ? "" : "s"} are losing value to monthly decay fees
                        </div>
                        <div className="mt-0.5 text-[11px] text-[#10281b]/70">
                            Recover the remaining Safe Vault balance now — spec §5 Phase 3.
                        </div>
                    </div>
                    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#10281b] px-4 py-1.5 text-[11.5px] font-semibold text-[#e6c985]">
                        Open Recovery
                        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="7 4 13 10 7 16" />
                        </svg>
                    </span>
                </Link>
            ) : null}

            <h2 className="mt-2 font-manrope text-[22px] font-semibold text-[#b98c23]">My Term Deposits</h2>

            <TabStrip
                tab={tab}
                setTab={setTab}
                counts={counts}
                search={search}
                setSearch={setSearch}
                sortKey={sortKey}
                setSortKey={setSortKey}
                filterCompound={filterCompound}
                setFilterCompound={setFilterCompound}
                filterLoan={filterLoan}
                setFilterLoan={setFilterLoan}
                filterLtv={filterLtv}
                setFilterLtv={setFilterLtv}
                filtersApplied={filtersApplied}
                clearFilters={clearFilters}
            />

            <TdTable
                rows={rows}
                isLoading={isLoading}
                compoundOverrides={compoundOverrides}
                onSetCompoundMode={setCompoundMode}
                onEarlyExit={requestEarlyExit}
                onCompoundNow={triggerCompoundNow}
                compoundNowBusyId={compoundNowBusyId}
                signedIn={Boolean(signer)}
            />

            <ConfirmModal
                open={exitTarget !== null}
                title={exitTarget ? `Confirm Early Exit – Term Deposit #${exitTarget.id}` : "Confirm Early Exit"}
                body={exitTarget ? <EarlyExitBreakdown deposit={exitTarget} /> : null}
                confirmLabel="Yes, Early Exit"
                cancelLabel="Cancel"
                tone="danger"
                busy={exitBusy}
                onConfirm={confirmEarlyExit}
                onCancel={cancelEarlyExit}
            />

            <FlowModal
                open={exitFlowOpen}
                title="Closing Deposit"
                subtitle="Confirm the wallet prompt to broadcast the early-exit transaction."
                steps={exitSteps}
            />

            <FlowModal
                open={compoundFlowOpen}
                title="Updating Automated Reinvestment"
                subtitle="Confirm the wallet prompt to set the new compound mode."
                steps={compoundSteps}
            />

            <ResultModal
                open={exitResult !== null}
                type={exitResult?.type}
                title={exitResult?.title || ""}
                message={exitResult?.message || ""}
                onClose={() => setExitResult(null)}
            />

            <ResultModal
                open={compoundError !== null}
                type="error"
                title="Automated Reinvestment update failed"
                message={compoundError || ""}
                onClose={() => setCompoundError(null)}
            />

            <ResultModal
                open={compoundSuccess !== null}
                type="success"
                title={compoundSuccess?.title || ""}
                message={compoundSuccess?.message || ""}
                onClose={() => {
                    setCompoundSuccess(null);
                    refresh().catch(() => {});
                }}
            />

            <FlowModal
                open={compoundNowFlowOpen}
                title="Compounding Interest"
                subtitle="Confirm the wallet prompt to reinvest the accrued interest into a new deposit."
                steps={compoundNowSteps}
            />

            <ResultModal
                open={compoundNowResult !== null}
                type={compoundNowResult?.type}
                title={compoundNowResult?.title || ""}
                message={compoundNowResult?.message || ""}
                onClose={() => {
                    setCompoundNowResult(null);
                    refresh().catch(() => {});
                }}
            />
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Tab strip + filters                                                       */
/* -------------------------------------------------------------------------- */

function TabStrip({
    tab,
    setTab,
    counts,
    search,
    setSearch,
    sortKey,
    setSortKey,
    filterCompound,
    setFilterCompound,
    filterLoan,
    setFilterLoan,
    filterLtv,
    setFilterLtv,
    filtersApplied,
    clearFilters
}) {
    return (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
            <div className="inline-flex items-center rounded-full bg-[#10281b] p-0.5">
                {TABS.map((t, idx) => {
                    const active = tab === t.id;
                    const prev = idx > 0 ? TABS[idx - 1] : null;
                    const showDivider = prev && tab !== prev.id && !active;
                    return (
                        <span key={t.id} className="inline-flex items-center">
                            {showDivider ? (
                                <span className="mx-1 h-4 w-px bg-[#e6c985]/30" aria-hidden="true" />
                            ) : null}
                            <button
                                onClick={() => setTab(t.id)}
                                className={`rounded-full px-6 py-1 text-[12px] font-semibold transition-colors ${
                                    active
                                        ? "bg-[#2d3d22] text-[#FBC206]"
                                        : "text-[#e6c985] hover:text-[#FBC206]"
                                }`}
                            >
                                {t.label} ({counts[t.id]})
                            </button>
                        </span>
                    );
                })}
            </div>

            <div className="flex flex-1 flex-wrap items-center gap-2 sm:gap-3 lg:max-w-2xl lg:flex-nowrap lg:justify-end lg:gap-4">
                <div className="flex min-w-[10rem] flex-1 items-center gap-2 rounded-full bg-transparent px-3 py-1 ring-1 ring-[#bfb67b]">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search here..."
                        className="flex-1 bg-transparent text-[12px] text-[#1D3E29] placeholder:text-[#1D3E29]/60 focus:outline-none"
                        aria-label="Search deposits"
                    />
                    <SearchIcon />
                </div>
                <FiltersMenu
                    filterCompound={filterCompound}
                    setFilterCompound={setFilterCompound}
                    filterLoan={filterLoan}
                    setFilterLoan={setFilterLoan}
                    filterLtv={filterLtv}
                    setFilterLtv={setFilterLtv}
                    applied={filtersApplied}
                    onClear={clearFilters}
                />
                <SortByMenu sortKey={sortKey} setSortKey={setSortKey} />
            </div>
        </div>
    );
}

/**
 * Generic outside-click + Escape handler for popovers. Returns a ref to
 * attach on the wrapper element and a state pair to control open/close.
 */
function usePopover() {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (event) => {
            if (!ref.current) return;
            if (!ref.current.contains(event.target)) setOpen(false);
        };
        const onKey = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);
    return { open, setOpen, ref };
}

function FiltersMenu({ filterCompound, setFilterCompound, filterLoan, setFilterLoan, filterLtv, setFilterLtv, applied, onClear }) {
    const { open, setOpen, ref } = usePopover();
    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[12px] font-semibold ring-1 transition-colors ${
                    applied
                        ? "bg-[#1D3E29] text-[#FBC206] ring-[#1D3E29]"
                        : "bg-transparent text-[#1D3E29] ring-[#bfb67b] hover:bg-[#1D3E29]/5"
                }`}
            >
                Filters
                {applied ? (
                    <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#FBC206] px-1 text-[9px] font-bold text-[#1D3E29]">
                        ON
                    </span>
                ) : (
                    <FiltersIcon />
                )}
            </button>

            {open ? (
                <div
                    role="menu"
                    className="absolute right-0 top-full z-30 mt-2 w-72 overflow-hidden rounded-[12px] bg-white ring-1 ring-[#bfb67b] shadow-xl"
                >
                    <div className="bg-[#1D3E29] px-4 py-2">
                        <div className="text-[9px] font-semibold uppercase tracking-widest text-[#FBC206]">
                            Filters
                        </div>
                        <div className="mt-0.5 font-manrope text-[12px] font-bold text-white">
                            Refine the visible rows
                        </div>
                    </div>
                    <div className="p-3 space-y-3">
                        <FilterSection
                            label="Compound mode"
                            options={COMPOUND_FILTERS}
                            value={filterCompound}
                            onChange={setFilterCompound}
                        />
                        <FilterSection
                            label="Loan"
                            options={LOAN_FILTERS}
                            value={filterLoan}
                            onChange={setFilterLoan}
                        />
                        <FilterSection
                            label="LTV band"
                            options={LTV_FILTERS}
                            value={filterLtv}
                            onChange={setFilterLtv}
                        />
                        <div className="flex items-center justify-between border-t border-[#1D3E29]/10 pt-3">
                            <button
                                type="button"
                                disabled={!applied}
                                onClick={onClear}
                                className="text-[10px] font-semibold text-[#1D3E29]/70 hover:text-[#1D3E29] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Clear filters
                            </button>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="rounded-full bg-[#1D3E29] px-3 py-1 text-[10px] font-semibold text-white"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function FilterSection({ label, options, value, onChange }) {
    return (
        <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-[#1D3E29]/65">
                {label}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
                {options.map((opt) => {
                    const active = value === opt.id;
                    return (
                        <button
                            key={opt.id}
                            type="button"
                            onClick={() => onChange(opt.id)}
                            className={`rounded-full px-2.5 py-0.5 text-[10.5px] font-semibold transition-colors ${
                                active
                                    ? "bg-[#1D3E29] text-[#FBC206]"
                                    : "bg-white text-black ring-1 ring-[#bfb67b] hover:bg-[#10281b]/5"
                            }`}
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function SortByMenu({ sortKey, setSortKey }) {
    const { open, setOpen, ref } = usePopover();
    const current = SORT_OPTIONS.find((o) => o.id === sortKey) || SORT_OPTIONS[0];
    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                className="inline-flex items-center gap-2 rounded-full bg-transparent px-3 py-1 text-[12px] font-semibold text-[#1D3E29] ring-1 ring-[#bfb67b] hover:bg-[#1D3E29]/5"
            >
                Sort: <span className="font-bold">{current.label}</span>
                <SortIcon />
            </button>

            {open ? (
                <div
                    role="menu"
                    className="absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-[12px] bg-white ring-1 ring-[#bfb67b] shadow-xl"
                >
                    <div className="bg-[#1D3E29] px-4 py-2">
                        <div className="text-[9px] font-semibold uppercase tracking-widest text-[#FBC206]">
                            Sort By
                        </div>
                    </div>
                    <ul className="p-2">
                        {SORT_OPTIONS.map((opt) => {
                            const active = opt.id === sortKey;
                            return (
                                <li key={opt.id}>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => {
                                            setSortKey(opt.id);
                                            setOpen(false);
                                        }}
                                        className={`flex w-full items-center justify-between rounded-[8px] px-3 py-1.5 text-left text-[11.5px] font-semibold transition-colors ${
                                            active
                                                ? "bg-[#1D3E29] text-white"
                                                : "text-[#1D3E29] hover:bg-[#10281b]/5"
                                        }`}
                                    >
                                        <span>{opt.label}</span>
                                        {active ? <CheckTiny /> : null}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : null}
        </div>
    );
}

function CheckTiny() {
    return (
        <svg className="h-2.5 w-2.5" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="2.5 6.5 5 9 9.5 3.5" />
        </svg>
    );
}

/* -------------------------------------------------------------------------- */
/*  Deposit table                                                             */
/* -------------------------------------------------------------------------- */

function TdTable({
    rows,
    isLoading,
    compoundOverrides,
    onSetCompoundMode,
    onEarlyExit,
    onCompoundNow,
    compoundNowBusyId,
    signedIn
}) {
    // 1-second ticker so each row's live Accrued Interest advances
    // visibly instead of sitting frozen at the indexed value.
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);
    // Keys off chain time, not wall clock — local-node testing
    // (Hardhat evm_increaseTime) shows correct accrual immediately
    // after a time warp.
    const { chainNowSec } = useChainTime();
    // eslint-disable-next-line no-unused-vars
    const _tick = tick;
    const nowSec = useMemo(() => chainNowSec, [chainNowSec, tick]);
    return (
        <div className="overflow-hidden rounded-[10px]">
            <div className="overflow-x-auto">
                <table className="min-w-full">
                    <thead>
                        <tr className="bg-[#10281b] text-[#e6c985]">
                            <Th>Bond ID/Name</Th>
                            <Th>Product</Th>
                            <Th>Principal</Th>
                            <Th>Accrued Interest</Th>
                            <Th>Time To Maturity</Th>
                            <Th>LTV</Th>
                            <Th>Automated Reinvestment</Th>
                            <Th sortable={false}>Action</Th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {isLoading ? (
                            // Render a few shimmer rows so the table has
                            // structure during the initial fetch instead
                            // of a single centered "Loading…" line.
                            Array.from({ length: 3 }).map((_, idx) => (
                                <SkeletonRow key={`skel-${idx}`} />
                            ))
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="px-4 py-6 text-center text-[12px] text-black/60">
                                    No positions in this category yet.
                                </td>
                            </tr>
                        ) : (
                            rows.map((row) => (
                                <TdRow
                                    key={row.id}
                                    row={row}
                                    nowSec={nowSec}
                                    compoundOverride={compoundOverrides[row.id]}
                                    onSetCompoundMode={(mode) => onSetCompoundMode(row.id, mode)}
                                    onEarlyExit={() => onEarlyExit?.(row)}
                                    onCompoundNow={() => onCompoundNow?.(row)}
                                    compoundNowBusy={compoundNowBusyId === row.id}
                                    signedIn={signedIn}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Th({ children, sortable = true }) {
    return (
        <th className="px-3 py-3 text-left font-manrope text-[11px] font-semibold whitespace-nowrap">
            <span className="inline-flex items-center gap-1.5">
                {children}
                {sortable ? <SortHeadIcon /> : null}
            </span>
        </th>
    );
}

/**
 * Placeholder row shown while `useDeposits` is fetching. Mirrors the
 * cell structure of TdRow so the table doesn't reflow when real rows
 * replace the skeletons.
 */
function SkeletonRow() {
    return (
        <tr className="border-b border-black/10 last:border-0">
            <td className="px-3 py-3">
                <Shimmer width="6rem" height="0.875rem" />
            </td>
            <td className="px-3 py-3">
                <Shimmer width="3.5rem" height="1rem" />
            </td>
            <td className="px-3 py-3 whitespace-nowrap">
                <Shimmer width="4rem" height="0.875rem" />
            </td>
            <td className="px-3 py-3 whitespace-nowrap">
                <Shimmer width="3.5rem" height="0.875rem" />
            </td>
            <td className="px-3 py-3 whitespace-nowrap">
                <Shimmer width="3rem" height="0.875rem" />
            </td>
            <td className="px-3 py-3">
                <Shimmer width="4.5rem" height="1.25rem" />
            </td>
            <td className="px-3 py-3">
                <Shimmer width="4rem" height="0.875rem" />
            </td>
            <td className="px-3 py-3">
                <Shimmer width="5rem" height="1.25rem" />
            </td>
        </tr>
    );
}

function TdRow({
    row,
    nowSec,
    compoundOverride,
    onSetCompoundMode,
    onEarlyExit,
    onCompoundNow,
    compoundNowBusy,
    signedIn
}) {
    const id = String(row.id).padStart(6, "0");
    const bondLabel = `#${id}/${row.txHash ? row.txHash.slice(2, 6).toUpperCase() : "D457"}...`;
    const principal = `$${formatUsdcCompact(row.principal)} USDC`;
    const now = nowSec ?? chainClockNowSec(); // chain clock, never wall time
    // Accrued Interest — per client (Jun 2026) this column should visibly
    // update in REAL TIME, not sit at $0.00 until a whole day ticks over.
    // For an ACTIVE deposit we therefore render the per-second smoother
    // (`liveAccruedInterestSmooth`), the same display path the Dashboard
    // headline cards use. NOTE: this is a DISPLAY figure that leads the
    // contract's day-stepped settlement value by up to ~1 day's accrual;
    // settlement / early-exit / liquidation paths MUST keep using the
    // day-stepped `liveAccruedInterest` so payouts are never overstated.
    // For matured / closed / exited rows the indexed value was already
    // finalised by the closing chain event, so we trust it.
    // §V6.2 — use the deposit's OWN annualRateBps (snapshotted at open).
    // Turbo deposits accrue at 27.5%, Standard at 20%; the global constant
    // would under-quote Turbo accrual on this page.
    const depRateBps = Number(row.annualRateBps) || config.protocol.annualRateBps;
    const liveAccrued =
        Number(row.state) === 1
            ? liveAccruedInterestSmooth(
                  row.principal,
                  row.startTime,
                  row.maturityTime,
                  now,
                  depRateBps,
                  row.realizedInterest
              )
            : BigInt(row.accruedInterest || 0);
    const accrued = `+$${formatUsdcCompact(liveAccrued)} USDC`;
    const remaining = Math.max(0, Number(row.maturityTime) - now);
    const days = Math.floor(remaining / 86400);
    // Effective compound mode = override if user changed it locally, else
    // the on-chain value from the deposit struct.
    const compoundMode = compoundOverride ?? Number(row.compoundMode); // 0 Off, 1 Standard, 2 Turbo
    // §V6.2 — product is set at creation and immutable. Pill + N/A LTV
    // key off productMode, NOT compoundMode (which is just the next-
    // sweep preference). Without this split, toggling Automated Reinvestment to
    // Off flipped a Turbo deposit's pill back to "Standard" and re-
    // exposed an LTV percentage on a position with no real borrow
    // headroom (its Safe Vault was already redeployed via leverage).
    const productMode = Number(row.productMode || 0); // 0 Standard, 1 Turbo
    const isActive = Number(row.state) === 1;
    const isTurboProduct = productMode === 1;
    // Hide the Early Exit CTA once the deposit has crossed its
    // maturityTime — the user can settle on the detail page for the
    // full principal + interest rather than forfeit 20% as penalty.
    const maturityReached = now >= Number(row.maturityTime || 0);
    const showEarlyExit = isActive && !maturityReached;

    // §V6.2 — LTV = outstanding loan / deposit's OWN Safe Vault slice.
    // The slice is read from the on-chain `safeVaultAmount` (Standard
    // = 80% of principal, Turbo = 50%) so the percentage is correct
    // for every product. The earlier global-bps formula under-counted
    // Turbo's collateral (assumed 80% but the chain stores 50%).
    const borrowed = BigInt(row.loanBalance);
    const safeVault = BigInt(row.safeVaultAmount || 0) > 0n
        ? BigInt(row.safeVaultAmount)
        : (BigInt(row.principal) * BigInt(config.protocol.safeVaultBps)) / 10000n;
    const ltvPct =
        safeVault > 0n
            ? Math.round(Number((borrowed * 10000n) / safeVault) / 100)
            : 0;
    // LTV is "N/A" when:
    //   - The deposit can never carry a loan (terminal state / zero
    //     collateral), OR
    //   - It's a Turbo product with no outstanding loan. Turbo's
    //     borrow capacity is conceptually pre-spent through the
    //     leverage flow, so a flat "0%" reads like idle headroom that
    //     doesn't actually exist. Showing N/A matches the product's
    //     "no fresh borrow capacity" framing on the Loans page.
    // A Turbo deposit WITH a real outstanding loan still surfaces
    // the live ratio — never hide actual risk the user has taken.
    const ltvUnavailable =
        !isActive ||
        safeVault === 0n ||
        (isTurboProduct && borrowed === 0n);

    // §17.2 compound trigger = 100 USDC accrued. Same for both modes:
    // Turbo compound now mints a SINGLE combined deposit funded by
    // `interest + 56%-of-interest borrow against source`, so the
    // combined principal (≥ $156 at the trigger) trivially clears the
    // §3.2 $100 MIN_DEPOSIT floor — no separate Turbo threshold needed.
    const COMPOUND_TRIGGER = 100n * 1_000_000n;
    const canCompoundNow =
        isActive && compoundMode > 0 && liveAccrued >= COMPOUND_TRIGGER;

    return (
        <tr className="border-b border-black/10 last:border-0">
            <td className="px-3 py-3">
                <span className="font-manrope text-[12px] font-normal text-black">{bondLabel}</span>
            </td>
            <td className="px-3 py-3">
                <TypePill type={isTurboProduct ? "Turbo" : "Standard"} />
            </td>
            <td className="px-3 py-3 whitespace-nowrap font-manrope text-[12px] font-normal text-black">{principal}</td>
            <td className="px-3 py-3 whitespace-nowrap font-manrope text-[12px] font-normal text-black">{accrued}</td>
            <td className="px-3 py-3 whitespace-nowrap font-manrope text-[12px] font-normal text-black">{days} Days</td>
            <td className="px-3 py-3">
                {/* §V6.2 — LTV is shown for every deposit (Standard or
                    Turbo) with a real loan against its on-chain
                    safeVaultAmount. N/A is reserved for the truly-
                    unborrowable cases: not Active, or zero collateral.
                    Turbo deposits with a loan now surface real risk
                    instead of being hidden behind a blanket N/A. */}
                <LtvBadge ltv={ltvPct} unavailable={ltvUnavailable} />
            </td>
            <td className="px-3 py-3">
                <CompoundToggle
                    mode={compoundMode}
                    productMode={productMode}
                    onChange={onSetCompoundMode}
                    disabled={!isActive}
                />
            </td>
            <td className="px-3 py-3">
                <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <Link
                        to={`/term-deposits/${row.id}`}
                        className="inline-flex h-7 items-center justify-center whitespace-nowrap rounded-full border border-[#1D3E29] px-3 text-[11px] font-semibold text-[#1D3E29] hover:bg-[#1D3E29] hover:text-white transition-colors"
                    >
                        View More
                    </Link>
                    {/* §I.6 V6.2 strict — per-deposit "Compound Now" is
                        gone. Compounding is now pool-driven only; the user
                        creates a Standard/Turbo pool from the Reinvest
                        page and attaches deposits, then the CYR keeper
                        sweeps the pool automatically once combined yield
                        crosses 102 USDC. */}
                    {showEarlyExit ? (() => {
                        // Contract reverts with OutstandingLoan(depositId) if
                        // the deposit still has an open loan — repay it from
                        // the My Loans page first.
                        const hasOpenLoan = BigInt(row.loanBalance) > 0n;
                        const disabled = !signedIn || hasOpenLoan;
                        // §V6.2 — penalty is per-product (Standard 20%,
                        // Turbo 50%). Mirror the confirmEarlyExit success
                        // copy (lines ~395-397) so the tooltip and the
                        // post-exit message always quote the same %.
                        const penaltyPct =
                            (Number(row.earlyExitPenaltyBps) ||
                                (isTurboProduct ? 5000 : 2000)) / 100;
                        const title = !signedIn
                            ? "Connect wallet to early exit"
                            : hasOpenLoan
                                ? "Repay the outstanding loan before early exit"
                                : `Close this Term Deposit now (${penaltyPct}% penalty applies)`;
                        return (
                            <button
                                type="button"
                                onClick={onEarlyExit}
                                disabled={disabled}
                                title={title}
                                className="inline-flex h-7 items-center justify-center gap-1 whitespace-nowrap rounded-full bg-[#C93030] px-3 text-[11px] font-semibold text-white hover:bg-[#A82626] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <ExitIcon />
                                Early Exit
                            </button>
                        );
                    })() : null}
                </div>
            </td>
        </tr>
    );
}

function ExitIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10 4 L10 10" />
            <path d="M5.5 6 A6 6 0 1 0 14.5 6" />
        </svg>
    );
}

function TypePill({ type }) {
    if (type === "Turbo") {
        return (
            <span className="inline-flex items-center justify-center rounded-full bg-[#10281b] px-4 py-0.5 font-manrope text-[11px] font-semibold text-white">
                Turbo
            </span>
        );
    }
    return (
        <span className="inline-flex items-center justify-center rounded-full bg-[#10281b] px-4 py-0.5 font-manrope text-[11px] font-semibold text-white">
            Standard
        </span>
    );
}

/**
 * Automated Reinvestment control. Clicking the toggle or label opens a dropdown of
 * modes (Off / Standard / Turbo) but does NOT fire a wallet transaction —
 * the wallet is only prompted when the user explicitly picks a mode from
 * the dropdown. This avoids accidental on-chain writes from a stray
 * toggle tap.
 *
 * Mode values mirror the AutoCompound contract enum:
 *   0 = Off, 1 = Standard, 2 = Turbo.
 */
/**
 * Pure ON/OFF shift toggle for Automated Reinvestment. Product selection happens
 * at deposit creation and is immutable — so the user only ever needs to
 * decide whether to compound. When ON, the new compoundMode is derived
 * from the deposit's productMode (Standard product → 1, Turbo product
 * → 2). When OFF, mode = 0. No dropdown, no extra wallet prompts for
 * "switch product" flows.
 */
function CompoundToggle({ mode, productMode = 0, onChange, disabled = false }) {
    const on = mode > 0;
    // Productmode: 0 = Standard, 1 = Turbo. compoundMode ON resolves
    // to 1 for a Standard product and 2 for a Turbo product so the
    // CYR pool the deposit can attach to matches its economics.
    const onCompoundMode = productMode === 1 ? 2 : 1;
    const label = on ? "On" : "Off";

    function flip() {
        if (disabled) return;
        const next = on ? 0 : onCompoundMode;
        if (next === mode) return;
        onChange?.(next);
    }

    return (
        <div className={`flex items-center gap-2 ${disabled ? "opacity-50" : ""}`}>
            <button
                type="button"
                aria-pressed={on}
                aria-disabled={disabled}
                onClick={flip}
                disabled={disabled}
                title={
                    disabled
                        ? "Automated Reinvestment can only be changed on Active deposits"
                        : on
                        ? "Turn Automated Reinvestment off"
                        : "Turn Automated Reinvestment on"
                }
                className={`relative inline-flex h-3.5 w-7 shrink-0 items-center rounded-full transition-colors ${
                    on ? "bg-[#1D3E29]" : "bg-[#1D3E29]/30"
                } ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
            >
                <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-sm transition-transform ${
                        on ? "translate-x-3.5" : "translate-x-0.5"
                    }`}
                />
            </button>
            <span
                className={`font-manrope text-[11px] font-medium ${
                    on ? "text-[#1D3E29]" : "text-[#1D3E29]/70"
                }`}
            >
                {label}
            </span>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Icons                                                                     */
/* -------------------------------------------------------------------------- */

function SortHeadIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="3 5 6 2 9 5" />
            <polyline points="3 7 6 10 9 7" />
        </svg>
    );
}

function SearchIcon() {
    return (
        <svg className="h-4 w-4 text-[#1D3E29]" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="9" cy="9" r="6" />
            <line x1="14" y1="14" x2="18" y2="18" />
        </svg>
    );
}

function FiltersIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="2" y1="4" x2="14" y2="4" />
            <line x1="4" y1="8" x2="12" y2="8" />
            <line x1="6" y1="12" x2="10" y2="12" />
        </svg>
    );
}

function SortIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="3" y1="5" x2="13" y2="5" />
            <line x1="5" y1="8" x2="11" y2="8" />
            <line x1="7" y1="11" x2="9" y2="11" />
        </svg>
    );
}

function ChevronRight() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="7 4 13 10 7 16" />
        </svg>
    );
}

